import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { nb } from 'date-fns/locale';
import {
  Alert,
  Button,
  Datepicker,
  Divider,
  Flex,
  FormField,
  Label,
  Loader,
  Select,
  Text,
  Textarea,
  Title,
} from '@gjensidige/builders-components';
import { Edit } from '@gjensidige/builders-icons';
import { HouseAndContents } from '@gjensidige/builders-icons/symbols';

import type { OfferSubmissionRequest, SupplierCaseResponse } from '../types';
import { api, ApiError } from '../utils/api';
import { formatDate, formatNumber } from '../utils/format';
import { ErrorCircleIcon } from '../icons/ErrorCircleIcon';
import { WarningOctagonIcon } from '../icons/WarningOctagonIcon';
import { CompletionIcon } from './CompletionIcon';

// ---------------------------------------------------------------------------
// Page state machine
// ---------------------------------------------------------------------------

type PageState =
  | { status: 'loading' }
  | { status: 'loaded'; caseData: SupplierCaseResponse }
  | { status: 'not-found' }
  | { status: 'expired' }
  | { status: 'already-submitted' }
  | { status: 'submitted' }
  | { status: 'denied' }
  | { status: 'error'; message: string };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a "HH:mm:ss" or "HH:mm" time string as "HH:mm". */
function formatTime(time: string | null): string | null {
  if (!time) return null;
  return time.slice(0, 5);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SupplierPortalPage() {
  const { token } = useParams({ from: '/vendor/$token' });
  const [state, setState] = useState<PageState>({ status: 'loading' });

  // Form field state — all prices stored and displayed as inkl. mva NOK
  const [befaringNok, setBefaringNok] = useState('');
  const [kjoringNok, setKjoringNok] = useState('');
  const [utprisNok, setUtprisNok] = useState<number | null>(null);
  const [originalBefaringNok, setOriginalBefaringNok] = useState('');
  const [originalKjoringNok, setOriginalKjoringNok] = useState('');
  const [originalOfferDate, setOriginalOfferDate] = useState('');
  const [housingType, setHousingType] = useState('');
  const [originalHousingType, setOriginalHousingType] = useState('');
  const [isHousingTypeEditing, setIsHousingTypeEditing] = useState(false);
  const [isBefaringEditing, setIsBefaringEditing] = useState(false);
  const [isKjoringEditing, setIsKjoringEditing] = useState(false);
  const [offerDate, setOfferDate] = useState('');
  const [offerTime, setOfferTime] = useState('');
  const [comment, setComment] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCommentError, setShowCommentError] = useState(false);

  useEffect(() => {
    api.supplier
      .getCase(token)
      .then((caseData) => {
        const befaring = String(caseData.leverandorprisNok ?? '');
        const kjoring = String(caseData.drivingFeeNok ?? '0');
        const date = caseData.requestedInspectionDate ?? '';
        const currentHousingType = caseData.housingType ?? '';
        const currentIsAvailable = caseData.availableHousingTypes.some(
          (option) => option.value === currentHousingType,
        );
        // If the order's current housing type is not among the assigned supplier's
        // available types (e.g. after a distance-only reroute), leave the select
        // empty so the supplier must actively pick one — otherwise the controlled
        // <select> would show the first option visually while React state stays
        // unset, permanently blocking submit.
        const initialHousingType = currentIsAvailable ? currentHousingType : '';
        setBefaringNok(befaring);
        setKjoringNok(kjoring);
        setUtprisNok(caseData.utprisNok);
        setOriginalBefaringNok(befaring);
        setOriginalKjoringNok(kjoring);
        setOriginalOfferDate(date);
        setHousingType(initialHousingType);
        setOriginalHousingType(currentHousingType);
        if (!currentIsAvailable) setIsHousingTypeEditing(true);
        setOfferDate(date);
        setOfferTime(formatTime(caseData.requestedInspectionTime) ?? '');
        setState({ status: 'loaded', caseData });
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          if (err.status === 404) setState({ status: 'not-found' });
          else if (err.status === 410) setState({ status: 'expired' });
          else if (err.status === 409) setState({ status: 'already-submitted' });
          else setState({ status: 'error', message: 'Noe gikk galt. Prøv igjen senere.' });
        } else {
          setState({ status: 'error', message: 'En uventet feil oppstod.' });
        }
      });
  }, [token]);

  const befaringChanged = befaringNok !== originalBefaringNok;
  const kjoringChanged = kjoringNok !== originalKjoringNok;
  const dateChanged = offerDate !== originalOfferDate;
  const housingTypeChanged = housingType !== originalHousingType;
  const needsComment = befaringChanged || kjoringChanged || dateChanged || housingTypeChanged;

  const totalNok = (Number(befaringNok) || 0) + (Number(kjoringNok) || 0);
  const computedCustomerPriceNok =
    utprisNok != null ? Math.max(utprisNok, Math.ceil(totalNok / 0.8)) : null;

  const handleSubmitOffer = async () => {
    setSubmitError(null);

    const befaringNum = Number(befaringNok);
    if (!befaringNok || isNaN(befaringNum) || befaringNum <= 0) {
      setSubmitError('Befaring-pris er påkrevd og må være større enn 0.');
      return;
    }
    const kjoringNum = Number(kjoringNok) || 0;
    if (!Number.isInteger(befaringNum) || !Number.isInteger(kjoringNum)) {
      setSubmitError('Prisene må oppgis i hele kroner.');
      return;
    }
    if (!selectedHousingType) {
      setSubmitError('Velg en boligtype leverandøren har pris for.');
      return;
    }

    if (needsComment && !comment.trim()) {
      setShowCommentError(true);
      return;
    }

    setShowCommentError(false);
    const body: OfferSubmissionRequest = {
      supplierPriceOre: Math.round(befaringNum * 100),
      drivingFeeOre: kjoringNum * 100,
      housingType: housingType || undefined,
      offeredDate: offerDate || undefined,
      offeredTime: offerTime || undefined,
      comment: comment.trim() || undefined,
    };

    setIsSubmitting(true);
    try {
      await api.supplier.submitOffer(token, body);
      setState({ status: 'submitted' });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 410) setState({ status: 'expired' });
        else if (err.status === 409) setState({ status: 'already-submitted' });
        else if (err.status === 422)
          setSubmitError('Valgt boligtype er ikke tilgjengelig. Velg en annen boligtype.');
        else if (err.status === 400) setSubmitError('Prisene må oppgis i hele kroner.');
        else setSubmitError('Noe gikk galt. Prøv igjen.');
      } else {
        setSubmitError('En uventet feil oppstod.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await api.supplier.denyCase(token);
      setState({ status: 'denied' });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 410) setState({ status: 'expired' });
        else if (err.status === 409) setState({ status: 'already-submitted' });
        else setSubmitError('Noe gikk galt. Prøv igjen.');
      } else {
        setSubmitError('En uventet feil oppstod.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Terminal states
  // -------------------------------------------------------------------------

  if (state.status === 'loading') {
    return (
      <Flex layout={1} justify="center" align="center" style={{ minHeight: '50vh' }}>
        <Loader variant="spinner" aria-label="Laster oppdrag" />
      </Flex>
    );
  }

  if (state.status === 'not-found') {
    return (
      <Alert severity="error">
        Oppdraget ble ikke funnet. Sjekk at du brukte riktig lenke fra e-posten din.
      </Alert>
    );
  }

  if (state.status === 'expired') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <WarningOctagonIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Svarfristen er utløpt
          </Title>
          <Text>Du kan dessverre ikke sende tilbud da svarfristen er utløpt.</Text>
        </Flex>
        <Text>Du kan lukke denne siden</Text>
      </Flex>
    );
  }

  if (state.status === 'already-submitted') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <CompletionIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Tilbud allerede sendt
          </Title>
          <Text>Du har allerede sendt inn et tilbud på dette oppdraget.</Text>
        </Flex>
        <Text>Du kan lukke denne siden</Text>
      </Flex>
    );
  }

  if (state.status === 'submitted') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <CompletionIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Tilbudet er sendt
          </Title>
          <Text>
            Nå har vi sendt tilbudet til kunden. Du får beskjed på e-post, når det er akseptert
          </Text>
        </Flex>
        <Text>Du kan lukke denne siden</Text>
      </Flex>
    );
  }

  if (state.status === 'denied') {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <ErrorCircleIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="2">
            Tilbudet er avslått
          </Title>
          <Text>
            Du oppga at du ikke kunne ta på deg oppdraget. Vi prøver å finne en annen leverandør
          </Text>
        </Flex>
        <Text>Du kan lukke denne siden</Text>
      </Flex>
    );
  }

  if (state.status === 'error') {
    return <Alert severity="error">{state.message}</Alert>;
  }

  // -------------------------------------------------------------------------
  // Loaded — main page
  // -------------------------------------------------------------------------

  const { caseData } = state;

  const inspectionTime = formatTime(caseData.requestedInspectionTime);
  const selectedHousingType = caseData.availableHousingTypes.find(
    (option) => option.value === housingType,
  );
  const selectedHousingTypeLabel = selectedHousingType?.label ?? caseData.buildingType ?? '—';

  const handleHousingTypeChange = (value: string) => {
    const option = caseData.availableHousingTypes.find((item) => item.value === value);
    if (!option) return;
    setHousingType(value);
    setBefaringNok(String(option.supplierPriceNok));
    setKjoringNok(String(option.drivingFeeNok));
    setUtprisNok(option.utprisNok);
    setIsHousingTypeEditing(false);
  };

  // Build "Om boligen" text lines matching the Figma layout
  const omBoligen: string[] = [];
  if (caseData.buildingType) omBoligen.push(`Boligtype: ${caseData.buildingType}`);
  if (caseData.propertySizeM2 != null) omBoligen.push(`Størrelse: ${caseData.propertySizeM2}m2`);
  if (caseData.propertyBathrooms != null) omBoligen.push(`Ant bad: ${caseData.propertyBathrooms}`);
  if (caseData.propertyFloors != null) omBoligen.push(`Ant etasjer: ${caseData.propertyFloors}`);
  if (caseData.propertyExtras?.length)
    omBoligen.push(`Andre ting: ${caseData.propertyExtras.join(', ')}`);
  if (caseData.propertyExtrasDescription) omBoligen.push(caseData.propertyExtrasDescription);

  // Build "Kontaktinfo" text lines
  const kontaktinfo: string[] = [];
  kontaktinfo.push(`Navn: ${caseData.customerName}`);
  if (caseData.customerEmail) kontaktinfo.push(`Epost: ${caseData.customerEmail}`);
  if (caseData.customerPhone) kontaktinfo.push(`Telefonnummer: ${caseData.customerPhone}`);

  return (
    <div className="supplier-page-bg">
      <Flex layout={1} gap="lg" style={{ maxWidth: '670px', margin: '0 auto', width: '100%' }}>
        {/* ── Header ───────────────────────────────────────────────────── */}
        <Flex layout={1} gap="sm" align="center" style={{ textAlign: 'center' }}>
          <HouseAndContents height={80} />
          <Title as="h1" size="3">
            Gi tilbud på befaringsoppdrag
          </Title>
          <Text>
            Hei {caseData.supplierName ?? 'leverandør'}! Du har fått et oppdrag. Les over detaljene
            og velg hva du ønsker å gjøre
          </Text>
        </Flex>

        <Divider color="200" />

        {/* ── Svarfrist ────────────────────────────────────────────────── */}
        <Flex layout={1} gap="xs" align="center" style={{ textAlign: 'center' }}>
          <Text weight={700}>Svarfrist innen</Text>
          <Text>{formatDate(caseData.deadlineIso)}</Text>
        </Flex>

        <Divider color="200" />

        {/* ── Detaljer om oppdraget ────────────────────────────────────── */}
        <Flex layout={1} gap="lg">
          <Title as="h2" size="4" style={{ textAlign: 'center' }}>
            Detaljer om oppdraget
          </Title>

          <div className="details-grid">
            {/* Row 1, Col 1 — Tid for befaring */}
            <Flex layout={1} gap="none">
              <Text weight={700}>Tid for befaring</Text>
              <Text size="small">
                {caseData.requestedInspectionDate
                  ? formatDate(caseData.requestedInspectionDate)
                  : 'Ingen dato'}
              </Text>
              {inspectionTime && <Text size="small">Klokken {inspectionTime}</Text>}
            </Flex>

            {/* Row 1, Col 2 — Adresse */}
            <Flex layout={1} gap="none">
              <Text weight={700}>Adresse</Text>
              <Text size="small">
                {caseData.address}, {caseData.zipCode} {caseData.city}
              </Text>
            </Flex>

            {/* Row 2, Col 1 — Om boligen */}
            {omBoligen.length > 0 ? (
              <Flex layout={1} gap="none">
                <Text weight={700}>Om boligen</Text>
                {omBoligen.map((line) => (
                  <Text key={line} size="small">
                    {line}
                  </Text>
                ))}
              </Flex>
            ) : (
              <div />
            )}

            {/* Row 2, Col 2 — Kontaktinfo */}
            <Flex layout={1} gap="none">
              <Text weight={700}>Kontaktinfo</Text>
              {kontaktinfo.map((line) => (
                <Text key={line} size="small">
                  {line}
                </Text>
              ))}
            </Flex>
          </div>
        </Flex>

        {/* ── Ditt tilbud ──────────────────────────────────────────────── */}
        <div className="supplier-offer-card">
          <Flex layout={1} gap="lg">
            <Title as="h2" size="4">
              Ditt tilbud
            </Title>
            {/* Building type */}
            <Flex layout={1} gap="xs">
              <Flex justify="space-between" align="center">
                <Label>Boligtype</Label>
                <Button
                  variant="action"
                  icon={<Edit />}
                  size="sm"
                  onClick={() => setIsHousingTypeEditing((value) => !value)}
                >
                  Endre
                </Button>
              </Flex>
              {isHousingTypeEditing ? (
                <FormField
                  as={Select}
                  label="Velg boligtype"
                  value={housingType}
                  onChange={(event) => handleHousingTypeChange(event.target.value)}
                >
                  <option value="" disabled>
                    Velg boligtype
                  </option>
                  {caseData.availableHousingTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </FormField>
              ) : (
                <div className="price-display-box">
                  <span>{selectedHousingTypeLabel}</span>
                </div>
              )}
              {housingTypeChanged && (
                <p className="inline-field-warning">
                  Du har foreslått en annen boligtype. Prisene er oppdatert. Skriv hvorfor den er
                  endret i kommentarfeltet.
                </p>
              )}
            </Flex>

            {/* Befaring */}
            <Flex layout={1} gap="xs">
              <Flex justify="space-between" align="center">
                <Label>Befaring</Label>
                <Button
                  variant="action"
                  icon={<Edit />}
                  size="sm"
                  onClick={() => setIsBefaringEditing((v) => !v)}
                >
                  {befaringChanged ? 'Endre tilbake' : 'Endre'}
                </Button>
              </Flex>
              {isBefaringEditing ? (
                <input
                  className="price-input"
                  type="number"
                  min={0}
                  value={befaringNok}
                  onChange={(e) => setBefaringNok(e.target.value)}
                  onBlur={() => setIsBefaringEditing(false)}
                />
              ) : (
                <div className="price-display-box">
                  <span>{befaringNok ? formatNumber(Number(befaringNok)) : '—'}</span>
                  <span className="price-unit">NOK inkl. mva</span>
                </div>
              )}
              {befaringChanged && (
                <p className="inline-field-warning">
                  Du har foreslått en annen pris. Skriv hvorfor den er endret i kommentarfeltet.
                </p>
              )}
            </Flex>

            {/* Kjøring */}
            <Flex layout={1} gap="xs">
              <Flex justify="space-between" align="center">
                <Label>Kjøring</Label>
                <Button
                  variant="action"
                  icon={<Edit />}
                  size="sm"
                  onClick={() => setIsKjoringEditing((v) => !v)}
                >
                  {kjoringChanged ? 'Endre tilbake' : 'Endre'}
                </Button>
              </Flex>
              {isKjoringEditing ? (
                <input
                  className="price-input"
                  type="number"
                  min={0}
                  value={kjoringNok}
                  onChange={(e) => setKjoringNok(e.target.value)}
                  onBlur={() => setIsKjoringEditing(false)}
                />
              ) : (
                <div className="price-display-box">
                  <span>{kjoringNok ? formatNumber(Number(kjoringNok)) : '—'}</span>
                  <span className="price-unit">NOK inkl. mva</span>
                </div>
              )}
              {kjoringChanged && (
                <p className="inline-field-warning">
                  Du har foreslått en annen kjørepris. Skriv hvorfor den er endret i
                  kommentarfeltet.
                </p>
              )}
            </Flex>

            {/* Date and time */}
            <Flex layout={{ xs: 1, sm: 2 }} gap="md">
              <Flex layout={1} gap="xs">
                <Label htmlFor="offer-date">Dato for befaring</Label>
                <div className="datepicker-wrap">
                  <Datepicker
                    id="offer-date"
                    data-builders-mode="internal"
                    locale={nb}
                    labelDropdownMonth="Velg måned"
                    labelDropdownYear="Velg år"
                    labelToggleButton="Åpne kalender"
                    value={offerDate}
                    onChange={(date) => {
                      if (date) setOfferDate(date.toISOString().slice(0, 10));
                    }}
                  />
                </div>
              </Flex>

              <Flex layout={1} gap="xs">
                <label className="native-label" htmlFor="offer-time">
                  Klokkeslett
                </label>
                <input
                  id="offer-time"
                  className="time-input"
                  type="time"
                  value={offerTime}
                  onChange={(e) => setOfferTime(e.target.value)}
                />
              </Flex>
            </Flex>
            {dateChanged && (
              <p className="inline-field-warning">
                Du har foreslått en ny dato. Skriv hvorfor du har endret den i kommentarfeltet.
              </p>
            )}

            {/* Comment */}
            <Flex layout={1} gap="xs">
              <Label htmlFor="offer-comment">Kommentar</Label>
              <Textarea
                id="offer-comment"
                placeholder="Legg ved kommentar til tilbudet..."
                rows={4}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  if (e.target.value.trim()) setShowCommentError(false);
                }}
              />
              {showCommentError && (
                <p className="inline-field-warning">
                  Du må legge til kommentar før du sender tilbud
                </p>
              )}
            </Flex>

            {/* Total */}
            <Flex layout={1} gap="xs" align="center" style={{ textAlign: 'center' }}>
              <Text weight={700}>Din pris</Text>
              <Text size="large" weight={700}>
                {formatNumber(totalNok)} NOK inkl. mva
              </Text>
            </Flex>
            <Flex layout={1} gap="xs" align="center" style={{ textAlign: 'center' }}>
              <Text size="small">
                Total pris:{' '}
                {computedCustomerPriceNok != null
                  ? `${formatNumber(computedCustomerPriceNok)} NOK inkl. mva`
                  : 'Ikke tilgjengelig'}
              </Text>
            </Flex>

            {submitError && <Alert severity="error">{submitError}</Alert>}

            {/* Short signing window warning — shown before submit when the customer will have < 7 days */}
            {state.status === 'loaded' && state.caseData.shortSigningWindow && (
              <Alert severity="warning">
                Vær oppmerksom på at befaringsdatoen er nær. Kunden vil ha begrenset tid til å svare
                på tilbudet etter at du sender det.
              </Alert>
            )}

            {/* Submit */}
            <Button onClick={handleSubmitOffer} disabled={isSubmitting} style={{ width: '100%' }}>
              {isSubmitting ? 'Sender...' : 'Send tilbud'}
            </Button>
          </Flex>
        </div>

        {/* ── Decline ──────────────────────────────────────────────────── */}
        <Flex
          layout={1}
          gap="xs"
          align="center"
          style={{ textAlign: 'center', paddingBottom: 'var(--builders-unit-40)' }}
        >
          <button
            className="decline-link"
            type="button"
            onClick={handleDeny}
            disabled={isSubmitting}
          >
            Jeg kan ikke ta på meg oppdraget
          </button>
          <Text size="small">Vi prøver å finne en annen leverandør</Text>
        </Flex>
      </Flex>
    </div>
  );
}
